import {
  createContext,
  useContext,
  useLayoutEffect,
  useState,
  type RefObject,
} from 'react';
import { GRID_GAP, GRID_ROW_PITCH } from '@/components/report-canvas/grid';
import { compactLayout } from '@/components/report-canvas/layout';
import type { GridLayout } from '@/components/report-canvas/types';

// ── Widgets never hide their data ─────────────────────────────────────────────
// A saved layout gives each widget a height in grid rows, but how much room
// its content needs depends on the width it gets: a table's rows wrap, a KPI
// title wraps at 1280px, everything stacks on a phone. Each widget measures
// its content's natural height and reports the rows it needs; the page shows
// the layout with every widget at least that tall, pushing what is beneath
// it down. The saved layout is not changed, so a wider screen gets the saved
// heights back.

/** Grid rows that hold `px` of content. */
export function rowsForHeight(px: number): number {
  return Math.max(1, Math.ceil((px + GRID_GAP) / GRID_ROW_PITCH));
}

/**
 * `layout` with each widget at least the rows its content needs, the grid
 * compacted so the ones below make room. Widgets that share a row (the same
 * top row and saved height, like a row of KPI tiles) grow together, so the
 * row stays even and what is beneath it still lines up.
 */
export function fitLayout(
  layout: GridLayout,
  needed: Readonly<Record<string, number>>,
): GridLayout {
  const rowKey = (item: GridLayout[number]) => `${item.y}:${item.h}`;
  const rowRows = new Map<string, number>();
  for (const item of layout) {
    const key = rowKey(item);
    rowRows.set(
      key,
      Math.max(rowRows.get(key) ?? item.h, needed[item.id] ?? 0),
    );
  }
  if (!layout.some((item) => (rowRows.get(rowKey(item)) ?? 0) > item.h))
    return layout;
  return compactLayout(
    layout.map((item) => {
      const rows = rowRows.get(rowKey(item)) ?? item.h;
      return rows > item.h ? { ...item, h: rows } : item;
    }),
  );
}

/**
 * The height `el`'s content takes when nothing stretches it: a flex column
 * adds up its children (and its padding, borders and gaps), anything else is
 * as tall as its content. An element marked `data-fit-min` (a chart, which
 * draws at whatever height it gets) counts as that many pixels.
 */
export function naturalHeight(el: HTMLElement): number {
  const min = el.dataset.fitMin;
  if (min != null) return Number(min);
  const style = getComputedStyle(el);
  if (style.display === 'none') return 0;
  const isColumn =
    style.display.includes('flex') && style.flexDirection === 'column';
  if (!isColumn) return el.scrollHeight;
  const children = [...el.children].filter(
    (child): child is HTMLElement =>
      child instanceof HTMLElement &&
      getComputedStyle(child).position !== 'absolute',
  );
  const box =
    parseFloat(style.paddingTop) +
    parseFloat(style.paddingBottom) +
    parseFloat(style.borderTopWidth) +
    parseFloat(style.borderBottomWidth);
  const gap = parseFloat(style.rowGap) || 0;
  const content = children.reduce(
    (sum, child) => sum + naturalHeight(child),
    0,
  );
  return box + content + gap * Math.max(0, children.length - 1);
}

/** Where a widget reports the rows its content needs. */
export const ContentFitContext = createContext<
  ((id: string, rows: number) => void) | null
>(null);

/**
 * Measures the element's natural height whenever it or anything in it
 * changes size, and reports it as grid rows for widget `id`.
 */
export function useReportContentRows(
  id: string,
  ref: RefObject<HTMLElement | null>,
) {
  const report = useContext(ContentFitContext);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !report) return;
    let frame = 0;
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() =>
        report(id, rowsForHeight(naturalHeight(el))),
      );
    };
    const resize = new ResizeObserver(measure);
    const watch = () => {
      resize.disconnect();
      resize.observe(el);
      // Deep enough to see a card's sections and a table's rows change; not
      // every chart mark.
      for (const child of el.querySelectorAll(
        ':scope > *, :scope > * > *, :scope > * > * > *, :scope > * > * > * > *',
      ))
        resize.observe(child);
    };
    watch();
    const mutations = new MutationObserver((records) => {
      // A chart redrawing its marks (a hover) changes nothing outside it.
      if (records.every((record) => record.target instanceof SVGElement))
        return;
      watch();
      measure();
    });
    mutations.observe(el, { childList: true, subtree: true });
    measure();
    return () => {
      cancelAnimationFrame(frame);
      resize.disconnect();
      mutations.disconnect();
    };
  }, [id, ref, report]);
}

/**
 * The rows each widget's content needs, as its widgets report them, and the
 * callback they report through (for {@link ContentFitContext}).
 */
export function useContentRows() {
  const [needed, setNeeded] = useState<Readonly<Record<string, number>>>({});
  const report = (id: string, rows: number) => {
    setNeeded((prev) => (prev[id] === rows ? prev : { ...prev, [id]: rows }));
  };
  return { needed, report };
}
